import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { CommonTranslationKeys } from 'intl/keys';
import { useDocumentationBrowseDialog } from 'components/common/withDocumentationBrowseDialog';
import {
  DocumentationBrowserDialog,
  getDocumentationLocalIdFromRowId,
  useDocumentationBrowserState,
} from '../DocumentationBrowser';
import { Store } from 'hooks/createStore';
import { SetValuesOptions } from 'components/dataCreationForms/withInputForm';
import { getLastSuccessfullVersion } from 'shared/domain/documentation/documentationVersionHelpers';
import { getCanBeUsedAsMap } from '../model';
import { normalizeDirectoryId } from 'shared/domain/directory/directoryIds';

export type SetValuesContext = () => SetValues;
interface SetValues {
  setValues: (
    key: string,
    value: { documentationId: string; versionId: string } | undefined,
    options?: SetValuesOptions  
  ) => void;
}

export interface DocumentationBrowseDialogProps {
  setValuesContext: SetValuesContext;
  formKey: string;
  setFilename: (name: string) => void;
  selectedStore: Store<string | undefined>;
  title: { name: string } | undefined;
  initialSelect: string | undefined;
  initialDirectory: string;
  /**
   * Directory of the selected file (or the initial one after cancel). The dialog
   * does not change the documentation grid's current directory.
   */
  onDirectoryChange?: (directoryId: string) => void;
}

export function DocumentationBrowseDialog({
  setValuesContext,
  formKey,
  setFilename,
  selectedStore,
  title,
  initialSelect,
  initialDirectory,
  onDirectoryChange,
}: DocumentationBrowseDialogProps): ReactElement {
  const { setValues } = setValuesContext();
  const { isOpenStore, closeDialog } = useDocumentationBrowseDialog();
  const [isOpen, setIsOpen] = useState(isOpenStore.get());
  const initialDocumentationLocalId = useMemo(
    () =>
      getDocumentationLocalIdFromRowId(initialSelect ?? '') ?? undefined,
    [initialSelect]
  );

  const browser = useDocumentationBrowserState({
    isOpen,
    selectableDocumentation: true,
    singleDocumentationSelection: true,
    initialDirectoryId: initialDirectory || undefined,
    initialDirectoryName: title?.name,
    initialDocumentationLocalId,
  });

  const selectedDocumentation = browser.selectedDocumentation;
  const selectedVersion = useMemo(
    () =>
      selectedDocumentation
        ? getLastSuccessfullVersion(selectedDocumentation)
        : undefined,
    [selectedDocumentation]
  );

  const enableSelect = useMemo(() => {
    if (!selectedDocumentation || !selectedVersion) return false;

    return getCanBeUsedAsMap(
      selectedVersion.extension,
      selectedVersion.imageConvertStatus
    );
  }, [selectedDocumentation, selectedVersion]);

  const mapCompatibleDocumentations = useMemo(
    () =>
      browser.documentations.filter((documentation) => {
        const version = getLastSuccessfullVersion(documentation);
        if (!version) {
          return false;
        }

        return getCanBeUsedAsMap(
          version.extension,
          version.imageConvertStatus
        );
      }),
    [browser.documentations]
  );

  const mapBrowser = useMemo(
    () => ({
      ...browser,
      documentations: mapCompatibleDocumentations,
    }),
    [browser, mapCompatibleDocumentations]
  );

  useEffect(() => {
    setIsOpen(isOpenStore.get());
    return isOpenStore.subscribe(() => {
      setIsOpen(isOpenStore.get());
    });
  }, [isOpenStore]);

  const onSubmit = useCallback(() => {
    if (!selectedDocumentation || !selectedVersion || !enableSelect) {
      return;
    }

    setValues(formKey, {
      documentationId: selectedDocumentation._id,
      versionId: selectedVersion._id,
    });

    setFilename(selectedDocumentation.name);
    onDirectoryChange?.(normalizeDirectoryId(selectedDocumentation.parentId));
    closeDialog();
  }, [
    selectedDocumentation,
    selectedVersion,
    enableSelect,
    setValues,
    formKey,
    setFilename,
    onDirectoryChange,
    closeDialog,
  ]);

  const closeAndDeselect = useCallback(() => {
    closeDialog();
    selectedStore.set(initialSelect);
    onDirectoryChange?.(initialDirectory);
  }, [
    closeDialog,
    selectedStore,
    initialSelect,
    initialDirectory,
    onDirectoryChange,
  ]);

  return (
    <DocumentationBrowserDialog
      isOpen={isOpen}
      onClose={closeAndDeselect}
      titleFallbackId={CommonTranslationKeys.documentation}
      searchDataQa='documentation-browse-dialog-search'
      browser={mapBrowser}
      confirmLabelId={CommonTranslationKeys.select}
      onConfirm={onSubmit}
      confirmDisabled={!enableSelect || browser.loading || Boolean(browser.loadError)}
      confirmLoading={browser.loading}
    />
  );
}
